// Basado en el Analizador Cabrillo original de XQ2OP.
// parse() extraído del bloque //PURE-START..//PURE-END de
// public/cabrillo/src/plantilla.html SIN cambios de algoritmo: misma
// heurística de detección de columna, mismo tratamiento CQ-WW,
// EE.UU./Canadá, indicativos portables y conteo de QSO inválidos (bad).
// Solo se modularizó (imports/exports) y se parametrizaron CT/ST en vez
// de closurarlos como const de módulo. Ver src/cabrillo/CREDITOS.md.
//
// Validado por equivalencia contra un snapshot congelado del
// comportamiento del original (scripts/cabrillo-equivalencia.mjs,
// tests/fixtures/cabrillo/CB1B.snapshot.json).

import { CONT } from './constantes.js';
import { bandOf, wl, look, isCall, grid } from './geo.js';

/**
 * Parsea un Cabrillo (texto completo) y devuelve el modelo crudo, o
 * { err } si no se pudo interpretar.
 * @param {string} text
 * @param {{e: object, p: object, c: Array}} CT - tabla CTY (ver datos.js)
 * @param {Object<string,[number,number]>} ST - estados/provincias EE.UU./Canadá
 */
export function parse(text, CT, ST) {
    const hdr = {};
    const rows = [];

    for (const ln of text.split(/\r?\n/)) {
        if (ln.startsWith('QSO:')) {
            const t = ln.trim().split(/\s+/);
            if (t.length >= 9) rows.push(t);
        } else {
            const m = ln.match(/^([A-Z0-9-]+):\s*(.*)$/);
            if (m && !(m[1] in hdr)) hdr[m[1]] = m[2].trim();
        }
    }

    if (!rows.length) {
        return { err: 'No encontré líneas QSO: en el archivo; ¿es un log Cabrillo?' };
    }

    const my = (hdr.CALLSIGN || rows[0][5]).toUpperCase();
    const vals = {};

    for (const t of rows) {
        for (let c = 6; c < Math.min(t.length, 16); c++) {
            const k = t[c].toUpperCase();
            if (k !== my && isCall(k) && look(k, CT)) (vals[c] = vals[c] || new Set()).add(k);
        }
    }

    const cols = Object.keys(vals).map(Number);
    const mx = Math.max(0, ...cols.map((c) => vals[c].size));
    const cc = cols.filter((c) => vals[c].size >= 0.6 * mx).sort((a, b) => a - b)[0];

    if (cc === undefined) {
        return { err: 'No pude identificar la columna del indicativo del corresponsal.' };
    }

    const cq = /CQ-WW/i.test(hdr.CONTEST || '');
    const ok = [];
    let bad = 0;

    for (const t of rows) {
        const m = t[3].match(/^(\d{4})-(\d\d)-(\d\d)$/);
        const h = (t[4] || '').padStart(4, '0');
        const r = look(t[cc] || '', CT);

        if (!m || !/^\d{4}$/.test(h) || !r) {
            bad++;
            continue;
        }

        const co = CT.c[r[0]];
        let z = r[1];
        let la = co[2];
        let lo = co[3];
        let lab = co[0];

        if (cq) {
            const x = parseInt(t[cc + 2]);
            if (x >= 1 && x <= 40) z = x;
        }

        if (lab == 'United States' || lab == 'Canada') {
            const s = t.slice(cc + 1).find((x) => ST[x.toUpperCase()]);
            if (s) {
                [la, lo] = ST[s.toUpperCase()];
                lab += ' · ' + s.toUpperCase();
            }
        }

        ok.push({
            ms: Date.UTC(+m[1], +m[2] - 1, +m[3], +h.slice(0, 2), +h.slice(2)),
            b: bandOf(t[1]),
            ci: r[0],
            cont: co[1],
            z,
            la,
            lo,
            lab,
        });
    }

    if (!ok.length) {
        return { err: 'Ningún QSO pudo asociarse a un país.' };
    }

    let t0 = ok[0].ms;
    for (const x of ok) if (x.ms < t0) t0 = x.ms;
    t0 = Math.floor(t0 / 36e5) * 36e5;

    const bands = [...new Set(ok.map((x) => x.b))].sort((a, b) => wl(a) - wl(b));
    const pm = {};
    const PL = [];
    const cm = {};
    const CN = [];

    const D = ok.map((x) => {
        if (!(x.lab in pm)) {
            pm[x.lab] = PL.length;
            PL.push([x.lab, x.la, x.lo]);
        }
        if (!(x.ci in cm)) {
            cm[x.ci] = CN.length;
            CN.push(CT.c[x.ci][0]);
        }
        return [
            Math.floor((x.ms - t0) / 6e5),
            bands.indexOf(x.b),
            Math.max(0, CONT.indexOf(x.cont)),
            cm[x.ci],
            x.z,
            pm[x.lab],
        ];
    });

    let NB = 1;
    for (const r of D) if (r[0] + 1 > NB) NB = r[0] + 1;

    let home = grid(hdr['GRID-LOCATOR']);
    if (!home) {
        const r = look(my, CT);
        if (r) home = [CT.c[r[0]][2], CT.c[r[0]][3]];
    }

    return { hdr, D, PL, CN, bands, t0, NB, home, bad, cc };
}
